import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AuthService } from '@cl/core/services';
import { AbsencesService } from '@cl/core/services/absences.service';
import { EmployeeNotificationsService } from '@cl/core/services/employee-notifications.service';
import { GroupPermissionsType } from '@cl/shared/group-permissions-type.enum';
import { AbsenceStatusType, EntityType } from '@cl/shared/models';
import { Absence } from '@cl/shared/models/absences/absence.model';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { UpsertAbsenceComponent } from '../upsert-absence/upsert-absence.component';

@Component({
    selector: 'cl-absence-actions',
    standalone: false,
    templateUrl: './absence-actions.component.html',
})
export class AbsenceActionsComponent {
    readonly AbsenceStatus = AbsenceStatusType;
    readonly EntityType = EntityType;

    @Input() id: number;
    @Input() performerId: number;
    @Input() status: string;
    @Input() showLuidaisLink: boolean = true;
    @Output() absenceChanged: EventEmitter<void> = new EventEmitter<void>();

    loading: boolean = false;

    get userPerformerIds(): number[] {
        return this.authService.userPerformerIds;
    }

    get hasMenuBookkeeperPermission(): boolean {
        return this.authService.userHasPermission([GroupPermissionsType.MENU_BOOKKEEPER]);
    }

    constructor(
        private authService: AuthService,
        private absencesService: AbsencesService,
        private employeeNotificationsService: EmployeeNotificationsService,
        private modalService: NzModalService,
        private messageService: NzMessageService,
    ) {}

    gotoLuidaisAbsence(): void {
        this.absencesService.gotoLuidaisAbsence(this.id);
    }

    async editAbsence(): Promise<void> {
        const editableObj: Absence = (await this.absencesService.getSingle(this.id)).data;
        const modal: NzModalRef<UpsertAbsenceComponent> = this.modalService.create({
            nzTitle: 'Prombūtnes labošana',
            nzContent: UpsertAbsenceComponent,
        });

        modal.afterOpen.subscribe(() => {
            modal.getContentComponent().setEditMode(editableObj);
        });

        modal.afterClose.subscribe((reload) => {
            if (reload) {
                this.absenceChanged.emit();
            }
        });
    }

    deleteAbsence(): void {
        this.modalService.confirm({
            nzTitle: 'Vai tiešām vēlaties dzēst prombūtni?',
            nzCancelText: 'Atcelt',
            nzOnOk: async () => {
                try {
                    this.loading = true;
                    await this.absencesService.delete(this.id);
                    this.absenceChanged.emit();
                    this.messageService.success('Prombūtne veiksmīgi dzēsta!');
                } catch (ex) {
                    if (ex.error?.errors?.length) {
                        ex.error.errors.forEach((error) => this.messageService.error(error));
                    } else {
                        this.messageService.error('Kļūda dzēšot prombūtni!');
                    }
                } finally {
                    this.loading = false;
                }
            },
        });
    }

    async sendAbsenceEmployeeNotification(): Promise<void> {
        try {
            this.loading = true;
            await this.employeeNotificationsService.sendEmployeeNotification(this.id, this.absencesService);
        } finally {
            this.loading = false;
        }
    }
}
